import {
  FEATURE_TO_SYNC_TYPE,
  SYNC_TYPE_TO_FEATURE,
} from '@/shared/features/jobs/jobs.constants';
import {
  DeviceSyncStatusEnum,
  DeviceSyncTypeEnum,
  FeatureEnum,
} from '@/shared/types/types.generated';

export const getSyncTypeFromFeature = (
  jobType: FeatureEnum,
): DeviceSyncTypeEnum | undefined => FEATURE_TO_SYNC_TYPE[jobType];

export const getFeatureFromSyncType = (
  syncType: DeviceSyncTypeEnum,
): FeatureEnum | undefined => SYNC_TYPE_TO_FEATURE[syncType];

export const SYNC_STATUS_VARIANT = {
  Target: 'target',
  Previous: 'previous',
} as const;

export type TJobSyncStatusVariant =
  (typeof SYNC_STATUS_VARIANT)[keyof typeof SYNC_STATUS_VARIANT];

export const DEVICE_SYNC_STATUSES_KEY = 'device_sync_statuses';

const SYNC_STATUS_KEYS_BY_VARIANT: Record<
  TJobSyncStatusVariant,
  readonly [string, string]
> = {
  [SYNC_STATUS_VARIANT.Target]: ['target_sync_status', 'targetSyncStatus'],
  [SYNC_STATUS_VARIANT.Previous]: [
    'previous_sync_status',
    'previousSyncStatus',
  ],
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const isDeviceSyncStatusEnum = (
  value: unknown,
): value is DeviceSyncStatusEnum =>
  typeof value === 'string' &&
  (Object.values(DeviceSyncStatusEnum) as string[]).includes(value);

export const getDeviceSyncStatusFromJob = (
  metaData: unknown,
  deviceId: string | undefined,
  variant: TJobSyncStatusVariant,
): DeviceSyncStatusEnum | undefined => {
  if (!deviceId || !isRecord(metaData)) return undefined;

  const all = metaData[DEVICE_SYNC_STATUSES_KEY];
  if (!isRecord(all)) return undefined;

  const entry = all[deviceId];
  if (!isRecord(entry)) return undefined;

  const [snakeKey, camelKey] = SYNC_STATUS_KEYS_BY_VARIANT[variant];
  const raw = entry[snakeKey] ?? entry[camelKey];
  return isDeviceSyncStatusEnum(raw) ? raw : undefined;
};

export const getPreviousJobDeletedFromJob = (
  metaData: unknown,
  deviceId: string | undefined,
): boolean => {
  if (!deviceId || !isRecord(metaData)) return false;

  const all = metaData[DEVICE_SYNC_STATUSES_KEY];
  if (!isRecord(all)) return false;

  const entry = all[deviceId];
  if (!isRecord(entry)) return false;

  return Boolean(entry.previous_job_deleted ?? entry.previousJobDeleted);
};

export const getTargetJobIdFromMetaData = (
  metaData: Record<string, unknown> | undefined,
  jobType: FeatureEnum,
): string | undefined => {
  if (!metaData) return undefined;

  const metaDataKeyMap: Record<string, string> = {
    [FeatureEnum.FtpBackup]: 'ftp_backup_target_job_id',
    [FeatureEnum.SmbBackup]: 'smb_backup_target_job_id',
    [FeatureEnum.DeviceSnapshot]: 'device_snapshot_target_job_id',
    [FeatureEnum.ReadVariableListFromDevice]:
      'read_variable_list_from_device_target_job_id',
  };

  const key = metaDataKeyMap[jobType];
  const value = key ? metaData[key] : undefined;
  return typeof value === 'string' ? value : undefined;
};

const STUCK_REASON_VALUES = new Set([
  'PENDING_TOO_LONG',
  'HEARTBEAT_STALE',
  'MAX_RUNTIME_EXCEEDED',
]);

export const getStuckReasonTranslationKey = (
  errorMessage: string | null | undefined,
): string | undefined => {
  if (!errorMessage || !STUCK_REASON_VALUES.has(errorMessage)) return undefined;
  return `pipelines:stuck_reasons.${errorMessage.toLowerCase()}`;
};

export const getRestoreJobBaseFeature = (jobType: FeatureEnum): FeatureEnum => {
  const featureMap: Record<string, FeatureEnum> = {
    [FeatureEnum.FtpRestore]: FeatureEnum.FtpBackup,
    [FeatureEnum.SmbRestore]: FeatureEnum.SmbBackup,
  };

  return featureMap[jobType] || jobType;
};
